import { Button, Drawer, Select } from '@pickleballinc/react-ui';
import clsx from 'clsx';
import { useRouter } from 'next/navigation';

import { Button as CustomButton } from '@/components/atoms/Button';
import { MobileSkillRange } from '@/components/molecules/MobileSkillRange';
import {
	AgeFilterOptions,
	getNewSearchParams,
	ISpectatorPagesFilter,
	pagesWithAgeFilter,
	pagesWithDistrictDropdown,
	pagesWithDivisionDropdown,
	pagesWithDoubleRatingFilter,
	pagesWithGenderFilter,
	pagesWithPlayerStatusDropdown,
	pagesWithSeasonDropdown,
	pagesWithSingleRatingFilter,
	pagesWithTeamsDropdown,
	transformAndFilterTeams
} from '@/components/organisms/SpectatorPagesFilter';
import { pageRoutes } from '@/components/organisms/SpectatorsPagesFilterWrapper';
import { Options } from '@/utils/types';

interface MobilePlayerFilterProps {
	currentRoute: string;
	filters: ISpectatorPagesFilter;
	gender: {
		label: string;
		value: string;
	};
	handleReset: () => void;
	setFilters: (filters: ISpectatorPagesFilter) => void;
	seasons: Options[];
	teams: Options[];
	divisions: Options[];
	districts: Options[];
	divisionsWithAllOption: Options[];
	districtsWithAllOption: Options[];
	teamsWithAllOption: Options[];
	playerStatusWithAllPlayersOption: Options[];
	useScheduleGroups: boolean;
}

export const MobilePlayerFilters = ({
	currentRoute,
	filters,
	gender,
	handleReset,
	setFilters,
	seasons,
	teams,
	divisions,
	districts,
	divisionsWithAllOption,
	districtsWithAllOption,
	teamsWithAllOption,
	playerStatusWithAllPlayersOption,
	useScheduleGroups
}: MobilePlayerFilterProps) => {
	const router = useRouter();

	if (!filters.mobile_filters) {
		return null;
	}

	const age =
		AgeFilterOptions.find((option: any) => {
			const split = filters.age ? filters.age.split('-').map((s) => Number(s)) : '';
			return split && split[0] === option.value[0] && split[1] === option.value[1];
		}) ?? AgeFilterOptions[0];

	const singlesSkill = filters.singles_skill ? filters.singles_skill.split('-').map((s) => Number(s)) : [0.0, 8.0];
	const doublesSkills = filters.doubles_skill ? filters.doubles_skill.split('-').map((s) => Number(s)) : [0.0, 8.0];

	const isSliderOpen = filters.mobile_filters === 'true';

	const updateSkillFilter = (values: number[], skillKey: string) => {
		setFilters({
			...filters,
			[skillKey]: values[0] === 0 && values[1] === 8 ? '' : values.join('-')
		});
	};

	return (
		<Drawer
			wrapperClassname={'w-full'}
			className="h-screen overflow-auto lg:hidden"
			title="Filters"
			open={isSliderOpen}
			onClose={() => setFilters({ ...filters, mobile_filters: '' })}
			onBackdropClick={() => setFilters({ ...filters, mobile_filters: '' })}
		>
			<div className="flex w-full flex-col bg-white px-4 pb-4 pt-1">
				{pagesWithDistrictDropdown.includes(currentRoute) && districts.length > 1 && (
					<div className="mt-6 w-full">
						<Select
							options={districtsWithAllOption}
							selectTheme="orange"
							id="districts-select"
							className="w-full border !rounded-md !border-gray-300"
							value={districtsWithAllOption.find(({ value }) => value === filters.district) || districtsWithAllOption[0]}
							onChange={(option) => {
								setFilters({ ...filters, district: (option as Options).value });
							}}
						/>
					</div>
				)}

				{pagesWithDivisionDropdown.includes(currentRoute) && divisions.length > 1 && (
					<div className="mt-6  w-full">
						<Select
							options={divisionsWithAllOption}
							selectTheme="orange"
							id="division-select"
							className="w-full border !rounded-md !border-gray-300"
							value={divisionsWithAllOption.find(({ value }) => value === filters.division) || divisionsWithAllOption[0]}
							onChange={(option) => {
								setFilters({ ...filters, division: (option as Options).value });
							}}
						/>
					</div>
				)}

				{pagesWithPlayerStatusDropdown.includes(currentRoute) && (
					<div className="mt-6  w-full">
						<Select
							options={playerStatusWithAllPlayersOption}
							placeholder="All players"
							selectTheme="orange"
							id="player-status-select"
							className="w-full border !rounded-md !border-gray-300"
							value={
								playerStatusWithAllPlayersOption.find(({ value }) => value === filters.player_status) ||
								playerStatusWithAllPlayersOption[0]
							}
							onChange={(option) => {
								setFilters({ ...filters, player_status: (option as Options).value });
							}}
						/>
					</div>
				)}

				{pagesWithSeasonDropdown.includes(currentRoute) && (
					<div className="mt-6 w-full">
						<Select
							options={seasons}
							selectTheme="orange"
							id="seasons-select"
							className="w-full border !rounded-md !border-gray-300"
							value={seasons.find(({ value }) => value === filters.season) || seasons[0]}
							onChange={(option) => {
								setFilters({ ...filters, season: (option as Options).value });
							}}
						/>
					</div>
				)}
				{pagesWithTeamsDropdown.includes(currentRoute) && (
					<div className="mt-6 w-full">
						<Select
							options={teamsWithAllOption}
							selectTheme="orange"
							id="teams-select"
							className="w-full border !rounded-md !border-gray-300"
							value={teamsWithAllOption.find(({ value }) => value === filters.team) || teamsWithAllOption[0]}
							onChange={(option) => {
								setFilters({ ...filters, team: (option as Options).value });
							}}
						/>
					</div>
				)}

				{((pageRoutes.GAMEDAY === currentRoute && !useScheduleGroups) || pageRoutes.EVENT === currentRoute) && (
					<div className="mt-6 w-full">
						<Select
							options={transformAndFilterTeams(teams)}
							selectTheme="orange"
							className="w-full border !rounded-md !border-gray-300"
							value={transformAndFilterTeams(teams).find(({ value }) => value === filters.team) || transformAndFilterTeams(teams)[0]}
							onChange={(option) => {
								setFilters({ ...filters, team: (option as Options).value });
							}}
						/>
					</div>
				)}

				{pagesWithAgeFilter.includes(currentRoute) && (
					<div className={clsx('mt-6 border-b border-gray-200', isSliderOpen ? 'flex flex-col' : 'hidden')}>
						<div className="mb-1 text-sm font-semibold text-gray-900">Age</div>
						<Select
							selectTheme="orange"
							options={AgeFilterOptions}
							onChange={(option: any) => setFilters({ ...filters, age: option.value.join('-') })}
							value={AgeFilterOptions.find(({ value }) => value === age.value) || AgeFilterOptions[0]}
							data-testid="players-age-select"
						/>
					</div>
				)}
				{pagesWithGenderFilter.includes(currentRoute) && (
					<div className="mt-6 flex flex-col border-b border-gray-200">
						<div className="mb-1 text-sm font-semibold text-gray-900">Gender</div>
						<div className="mb-4 flex items-center gap-x-4">
							<Button
								variant={gender.value === 'M' ? `primary` : `secondary`}
								className={`!flex !flex-1 !justify-center !text-sm ${gender.value === 'M' && '!bg-gray-900'}`}
								onClick={() => setFilters({ ...filters, gender: gender.value === 'M' ? '' : 'M' })}
								data-testid="players-gender-option-Male"
							>
								Male
							</Button>
							<Button
								variant={gender.value === 'F' ? `primary` : `secondary`}
								className={`!flex !flex-1 !justify-center !text-sm ${gender.value === 'F' && '!bg-gray-900'}`}
								onClick={() => setFilters({ ...filters, gender: gender.value === 'F' ? '' : 'F' })}
								data-testid="players-gender-option-Female"
							>
								Female
							</Button>
						</div>
					</div>
				)}

				{pagesWithSingleRatingFilter.includes(currentRoute) && (
					<div className="mt-4 flex flex-col border-b border-gray-200" data-testid="players-singles-skill-range">
						<div className="mb-8 text-sm font-semibold text-gray-900">Single Rating</div>
						<MobileSkillRange setSkillRange={(values) => updateSkillFilter(values, 'singles_skill')} rangeValue={singlesSkill} />
					</div>
				)}

				{pagesWithDoubleRatingFilter.includes(currentRoute) && (
					<div className="mt-4 flex flex-col border-b border-gray-200" data-testid="players-doubles-skill-range">
						<div className="mb-8 text-sm font-semibold text-gray-900">Double Rating</div>
						<MobileSkillRange setSkillRange={(values) => updateSkillFilter(values, 'doubles_skill')} rangeValue={doublesSkills} />
					</div>
				)}

				<div className="mt-4 flex items-center justify-end">
					<Button variant="secondary" className="mr-2 flex text-sm" onClick={handleReset} data-testid="players-button-reset">
						Reset
					</Button>
					<CustomButton
						className={'!flex  h-10 w-fit !justify-center !text-sm bg-orange-500 hover:bg-orange-700 text-white'}
						onClick={() => {
							setFilters({ ...filters, mobile_filters: '' });
							router.replace(`?${getNewSearchParams(filters)?.toString()}`);
						}}
						data-testid="players-button-apply"
					>
						Apply
					</CustomButton>
				</div>
			</div>
		</Drawer>
	);
};
